import { ActionIcon, Menu } from "@mantine/core";
import { ArrowUpDown, Check } from "lucide-react";
import { Fragment } from "react";

import classes from "./SortMenu.module.css";

export interface SortOption<T extends string> {
  value: T;
  label: string;
}

/** Live's titled group of orders (`dropdown-select`'s `nested`): "Created Date" over its two. */
export interface SortGroup<T extends string> {
  label: string;
  options: readonly SortOption<T>[];
}

export interface SortMenuProps<T extends string> {
  /** The orders offered, in live's words and order, alone or in live's titled groups. */
  options: readonly (SortOption<T> | SortGroup<T>)[];
  value: T;
  onChange: (value: T) => void;
}

/**
 * Live's sort over a gallery (`dropdown-select` on `ArrowUpDown`, capture gaps/28): an icon
 * button named "Sort" that opens the orders, the chosen one checked. A group's title is live's
 * menu label with a rule after it, as over the products' orders (spec A3d).
 */
export function SortMenu<T extends string>({ options, value, onChange }: SortMenuProps<T>) {
  const item = (option: SortOption<T>) => (
    <Menu.Item
      key={option.value}
      data-active={option.value === value || undefined}
      rightSection={option.value === value ? <Check size={16} /> : null}
      onClick={() => onChange(option.value)}
    >
      {option.label}
    </Menu.Item>
  );
  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="text" size="lg" aria-label="Sort">
          <ArrowUpDown size={20} strokeWidth={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {options.map((option) =>
          "options" in option ? (
            <Fragment key={option.label}>
              <Menu.Label className={classes.group}>
                {option.label}
                <span className={classes.rule} aria-hidden />
              </Menu.Label>
              {option.options.map((each) => item(each))}
            </Fragment>
          ) : (
            item(option)
          ),
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
